import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {filter, forkJoin, map, Subscription} from "rxjs";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {FUIMultiselectControlComponent, FUIStackedPageService} from "@fedeltapointofsale/ui";
import {EntityData, SearchResult} from "@fedeltapointofsale/data-client";
import {ExtendedSearchParams, SearchType} from "../search/search-page/extended-search-params";
import {AbstractSelectControlComponent, SelectControlSelector} from "./abstract-select-control.component";
import {EditParams} from "../edit/edit-page/edit-params";
import {EditMode} from "../edit/edit-page/edit-mode";
import {AnalyticsService} from "../../../analytics/analytics.service";
import {AnalyticEvent} from "../../../analytics/analytic-event";
import {SearchControlSelectObjects} from "./search-control-select-objects";
import {TranslateService} from "@ngx-translate/core";
import {ObjectUtils} from "../../utils/object-utils";

const CUSTOM_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiSelectControlComponent),
    multi: true
};

@Component({
    selector: "wbo-multi-select-control",
    templateUrl: "multi-select-control.component.html",
    providers: [
        CUSTOM_VALUE_ACCESSOR,
        {
            provide: SelectControlSelector,
            useExisting: forwardRef(() => MultiSelectControlComponent),
            multi: true
        }
    ]
})
export class MultiSelectControlComponent extends AbstractSelectControlComponent implements OnDestroy, OnChanges, AfterContentInit {

    @ViewChild(FUIMultiselectControlComponent) selectControlComponent: FUIMultiselectControlComponent;

    /**
     * Defines the value type. When true it is expecting a list of objects, otherwise a list of ids
     */
    @Input() multiObjectValueType: boolean;

    @Input() pillRemovable: boolean = true;
    @Input() showSelections: boolean = true;

    @Input() overrideOpenView: boolean = false;

    @Input() summaryLabelField: string;

    @Input() viewFilter: (item: any) => boolean;

    /**
     * Defines an initial default value. It will not trigger the valueChange unless notifyInitialDefault is specified.
     */
    @Input() initialDefaultValue: any;
    @Input() notifyInitialDefault: boolean = false;

    @Output() onSelect: EventEmitter<any[]> = new EventEmitter();
    @Output() onClear: EventEmitter<any> = new EventEmitter();

    private stackedViewSubscription: Subscription;
    private stackedViewAddSubscription: Subscription;
    private _selectControlValues: any[];
    // keep the full select control values (used when using viewFilter)
    private _fullSelectControlValues: any[];

    constructor(injector: Injector,
                changeDetector: ChangeDetectorRef,
                translateService: TranslateService,
                private stackedPageService: FUIStackedPageService,
                private analyticsService: AnalyticsService) {
        super(changeDetector, translateService);
    }

    ngAfterContentInit(): void {
        if (this.initialDefaultValue && this._value == undefined) {
            setTimeout(() => {
                if (this.notifyInitialDefault) {
                    this.value = this.initialDefaultValue;
                } else {
                    this._value = this.initialDefaultValue;
                }
                this.evaluateValueChange();
                this.changeDetector.detectChanges();
            });
        }
    }

    get selectControlValues(): any[] {
        return this._selectControlValues;
    }

    set selectControlValues(value: any[]) {
        this._selectControlValues = value;
        if (this._selectControlValues) {
            if (this.viewFilter) {
                value = this.restoreValuesUsingViewFilter(value);
            }
            this._fullSelectControlValues = value;
            if (this.multiObjectValueType) {
                if (!ObjectUtils.arraysEqual(value, super.value, this.valueField)) {
                    super.value = value;
                    this.onSelect.emit(value);
                }
            } else {
                const values: any[] = value.map((item) => item[this.valueField]);
                if (!ObjectUtils.arraysEqual(super.value, values)) {
                    super.value = values;
                    this.onSelect.emit(value);
                }
            }
        } else {
            this._fullSelectControlValues = undefined;
        }
    }

    restoreValuesUsingViewFilter(value: any[]): any[] {
        return this._fullSelectControlValues ? this._fullSelectControlValues.filter(item => !this.viewFilter(item)).concat(value) : value;
    }

    openExtendedSearch(): void {
        if (this.stackedViewSubscription) {
            this.stackedViewSubscription.unsubscribe();
        }
        this.stackedViewSubscription = this.stackedPageService.open(this.extendedSearchComponent,
            new ExtendedSearchParams(SearchType.MULTIPLE_SELECTION, this.extendedSearchComponentParams)).subscribe(results => {
            this.loadDataFromSearchModel();
            this._selectControlValues = results;
            this.changeDetector.detectChanges();
        });

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_SELECT_EXTENDED_SEARCH, this.sourceSearchModel);
    }

    openAdd(): void {
        if (this.stackedViewAddSubscription) {
            this.stackedViewAddSubscription.unsubscribe();
        }
        this.stackedViewAddSubscription = this.stackedPageService.open(this.extendedEditComponent, new EditParams(undefined, EditMode.ADD, null, this.extendedEditComponentParams)).subscribe((entity: EntityData<any>) => {
            this.loadDataFromSearchModel();
            if (this._selectControlValues) {
                this._selectControlValues = this._selectControlValues.slice(0);
                this._selectControlValues.push(entity);
            } else {
                this._selectControlValues = [entity];
            }
            this.changeDetector.detectChanges();
        });

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_SELECT_ADD_NEW, this.sourceSearchModel);
    }

    openView(option: any, allowOverride: boolean = false, identifier?: any): void {
        if (this.overrideOpenView && allowOverride) {
            this.onOptionOpen.emit(option);
        } else {
            this.stackedPageService.open(this.extendedEditComponent, new EditParams(identifier || this.sourceSearchModel.getIdentifier(option), EditMode.VIEW));
            this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_SELECT_VIEW_EXISTING, this.sourceSearchModel);
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.stackedViewSubscription) {
            this.stackedViewSubscription.unsubscribe();
        }
        if (this.stackedViewAddSubscription) {
            this.stackedViewAddSubscription.unsubscribe();
        }
    }

    click() {
        this.selectControlComponent.click();
    }

    focus() {
        this.selectControlComponent.focus();
    }

    /**
     * Compares the values as arrays
     * @param value1
     * @param value2
     * @returns {boolean}
     */
    override valueEqual(value1: any, value2: any): boolean {
        return !(value1 == undefined || value2 == undefined || value1.length !== value2.length || !value1.every((v, i) =>
            this.multiObjectValueType ? v[this.valueField] === value2[i][this.valueField] : v === value2[i]));
    }

    evaluateValueChange(): void {
        if (this.value == undefined) {
            this._selectControlValues = this.value;
            this._fullSelectControlValues = this.value;
        } else {
            if (!Array.isArray(this.value)) {
                throw new Error("Value is not an array");
            }
            if (this.value.length === 0) {
                this._selectControlValues = this.value;
                this._fullSelectControlValues = this.value;
                return;
            }
            // if is a multi object just use the array as the value
            if (this.multiObjectValueType) {
                this.assignSelectControlValuesFromFullList(this.value);
            } else if (this._selectControlValues == undefined || !ObjectUtils.arraysEqual(this._fullSelectControlValues.map((item) => item[this.valueField]), this.value)) {
                // Only look for the items if they are different
                // search for all the ids and build the list of currently selected records
                if (this.sourceSearchModel) {
                    this.subscriptions.add(forkJoin(
                        this.value.map((item) => {
                            return this.sourceSearchModel.searchRecords({[this.valueSearchField || this.valueField]: item}, undefined, 2, undefined);
                        })).pipe(
                        map((searchResults) =>
                            searchResults.map((searchResult: SearchResult) => {
                                if (searchResult.totalCount === 1) {
                                    return searchResult.data[0];
                                } else {
                                    console.log("Error finding the selected record for searchModel " + this.sourceSearchModel.getResourceName() + " with primary key " + this.value + ". Verify that the primary key " + this.valueField + " is included in the filters.");
                                    return undefined;
                                }
                            })),
                        filter((results) => results !== undefined)
                    ).subscribe((results) => {
                        this.assignSelectControlValuesFromFullList(results);
                        this.changeDetector.detectChanges();
                    }));
                } else if (this.data && this.data.length > 0) {
                    const values = [];
                    this.value.forEach((item) => {
                        const record: any = this.data.find(value => value[this.valueField] === item);
                        if (record) {
                            values.push(record);
                        }
                    });
                    this.assignSelectControlValuesFromFullList(values);
                }
            }
        }
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes["value"] || changes["data"]) {
            this.evaluateValueChange();
        }
    }

    override writeValue(obj: any): void {
        super.writeValue(obj);
        this.evaluateValueChange();
    }

    override setSearchControlSelectObjects(searchControlSelectObjects: SearchControlSelectObjects): void {
        setTimeout(() => {
            if (this.valueField) {
                const values = [];
                searchControlSelectObjects.objects.forEach(value => {
                    const selectObject = {};
                    selectObject[this.valueField] = value.id;
                    selectObject[this.labelField] = value.name;
                    values.push(selectObject);
                });
                this.selectControlValues = values;
            }
        });
    }

    assignSelectControlValuesFromFullList(values: any[]): void {
        this._fullSelectControlValues = values;
        if (this.viewFilter) {
            this._selectControlValues = values.filter(value => this.viewFilter(value));
        } else {
            this._selectControlValues = values;
        }
    }
}
